import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, Settings, Target, Users, Building2, UserCircle, Check, 
  Loader2, Save, Circle,
  AlignLeft, AlignCenter, AlignRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { ACCESS_OPTIONS, DEFAULT_BRANDING, SR_DEFAULTS } from './visualizationConstants';
import { ColorInput } from './ColorInput';
import { BrandingPreview } from './BrandingPreview';

const API = process.env.REACT_APP_BACKEND_URL;

const AUDIENCE_MAP = {
  board: { label: 'Board', icon: Target, description: 'Executive-level strategic overview' },
  team: { label: 'Team', icon: Users, description: 'Internal team progress tracking' },
  customer: { label: 'Customer', icon: Building2, description: 'Customer-facing roadmap updates' },
  custom: { label: 'Custom', icon: UserCircle, description: 'Custom audience with specific context' },
};

const VisualizationSettingsModal = ({ visualization, onClose, onSave, canEditContent = false }) => {
  const { token, organization } = useAuth();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  const [saving, setSaving] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [products, setProducts] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [brandingTemplates, setBrandingTemplates] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  const [formData, setFormData] = useState({
    name: visualization?.name || '',
    target_audience: visualization?.target_audience || 'team',
    audience_context: visualization?.audience_context || '',
    product_ids: visualization?.product_ids || [],
    branding_template_id: visualization?.branding_template_id || null,
    branding_overrides: { ...DEFAULT_BRANDING, ...(visualization?.branding_template?.branding || {}), ...(visualization?.branding_overrides || {}) },
    access_control: visualization?.access_control || 'public',
    allowed_user_ids: visualization?.allowed_user_ids || [],
  });

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, usersRes, templatesRes] = await Promise.all([
        fetch(`${API}/api/products`, { credentials: 'include' }),
        fetch(`${API}/api/visualizations/meta/org-users`, { credentials: 'include' }),
        fetch(`${API}/api/visualizations/branding-templates`, { credentials: 'include' }),
      ]);
      if (productsRes.ok) setProducts(await productsRes.json());
      if (usersRes.ok) setOrgUsers(await usersRes.json());
      if (templatesRes.ok) setBrandingTemplates(await templatesRes.json());
    } catch (error) { console.error('Error fetching data:', error); }
    finally { setLoadingData(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleUser = (id) => setFormData(prev => ({ ...prev, allowed_user_ids: prev.allowed_user_ids.includes(id) ? prev.allowed_user_ids.filter(x => x !== id) : [...prev.allowed_user_ids, id] }));
  const updateBranding = (field, value) => setFormData(prev => ({ ...prev, branding_overrides: { ...prev.branding_overrides, [field]: value } }));
  const applyTemplate = (t) => setFormData(prev => ({ ...prev, branding_template_id: t?.template_id || null, branding_overrides: { ...DEFAULT_BRANDING, ...(t?.branding || {}) } }));

  const handleSaveAsTemplate = async () => {
    if (!newTemplateName.trim()) { toast.error('Please enter a template name'); return; }
    setSavingTemplate(true);
    try {
      const res = await fetch(`${API}/api/visualizations/branding-templates`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: newTemplateName.trim(), branding: formData.branding_overrides, is_default: false }) });
      if (res.ok) { toast.success('Template saved'); setShowSaveTemplate(false); setNewTemplateName(''); fetchData(); }
      else toast.error('Failed to save template');
    } catch { toast.error('Failed to save template'); }
    finally { setSavingTemplate(false); }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('Please enter a name'); return; }
    if (formData.product_ids.length === 0) { toast.error('Please select at least one product'); return; }
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/visualizations/${visualization.visualization_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(formData) });
      if (response.ok) { const updated = await response.json(); toast.success('Settings saved'); onSave(updated); onClose(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to save settings'); }
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const audience = AUDIENCE_MAP[formData.target_audience] || AUDIENCE_MAP.team;
  const AudienceIcon = audience.icon;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[1050px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" /> Visualization Settings
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="access">Access</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto py-4">
            {/* General Tab */}
            <TabsContent value="general" className="mt-0 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Visualization Name</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} disabled={!canEditContent} className={cn(colors.bgSecondary)} data-testid="settings-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Target Audience</Label>
                <div className={cn("flex items-center gap-3 p-3 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
                  <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <AudienceIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className={cn("text-sm font-medium", colors.textPrimary)}>{audience.label}</p>
                    <p className={cn("text-xs", colors.textMuted)}>{audience.description}</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products" className="mt-0 space-y-4">
              <div><Label>Select a Product</Label><p className={cn("text-xs mt-1 mb-4", colors.textMuted)}>Choose the product for this visualization.</p></div>
              {loadingData ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : (
                <Select value={formData.product_ids[0] || ''} onValueChange={(v) => setFormData(prev => ({ ...prev, product_ids: [v] }))}>
                  <SelectTrigger className="w-full" data-testid="product-select">
                    <SelectValue placeholder="Select a product..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.product_id} value={product.product_id} data-testid={`product-${product.product_id}`}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </TabsContent>

            {/* Branding Tab */}
            <TabsContent value="branding" className="mt-0">
              {/* Template row */}
              <div className="flex items-center gap-2 mb-3">
                <Label className="text-xs whitespace-nowrap">Template:</Label>
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => applyTemplate(null)}
                    className={cn("px-2.5 py-1 rounded-full text-xs border transition-all",
                      !formData.branding_template_id ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300" : cn(colors.borderPrimary, colors.textMuted)
                    )} data-testid="template-none">Custom</button>
                  {brandingTemplates.map(t => (
                    <button key={t.template_id} type="button" onClick={() => applyTemplate(t)}
                      className={cn("px-2.5 py-1 rounded-full text-xs border transition-all flex items-center gap-1",
                        formData.branding_template_id === t.template_id ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300" : cn(colors.borderPrimary, colors.textMuted)
                      )} data-testid={`template-${t.template_id}`}>
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: t.branding?.primary_color || '#8b5cf6' }} />
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview above sub-tabs */}
              <div className="mb-3">
                <BrandingPreview branding={{ ...formData.branding_overrides, logo_url: organization?.logo_url || '' }} />
              </div>

              {/* Sub-tabs */}
              <Tabs defaultValue="style" className="w-full">
                <TabsList className="grid w-full grid-cols-3 h-8">
                  <TabsTrigger value="style" className="text-xs">Style</TabsTrigger>
                  <TabsTrigger value="colors" className="text-xs">Colors</TabsTrigger>
                  <TabsTrigger value="logo" className="text-xs">Logo</TabsTrigger>
                </TabsList>

                <TabsContent value="style" className="mt-3 space-y-3">
                  <div className="flex gap-2">
                    {[
                      { value: 'icons', label: 'Icons', icon: <Circle className="w-3.5 h-3.5" /> },
                      { value: 'bullets', label: 'Bullets', icon: <div className="w-2 h-2 rounded-full bg-current" /> },
                    ].map(opt => (
                      <button key={opt.value} type="button" onClick={() => updateBranding('display_style', opt.value)}
                        className={cn("flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all",
                          formData.branding_overrides.display_style === opt.value ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : cn(colors.borderPrimary, colors.bgSecondary)
                        )} data-testid={`display-style-${opt.value}`}>{opt.icon} {opt.label}</button>
                    ))}
                  </div>
                  <Label className="text-xs pt-1">Connector Lines</Label>
                  <div className="space-y-3">
                    {[1, 2, 3].map(level => {
                      const config = formData.branding_overrides.line_config || DEFAULT_BRANDING.line_config;
                      const lc = config.find(c => c.level === level) || { enabled: true, style: 'dashed' };
                      const updateLevel = (field, value) => {
                        const updated = config.map(c => c.level === level ? { ...c, [field]: value } : c);
                        updateBranding('line_config', updated);
                      };
                      return (
                        <div key={level} className="space-y-1.5">
                          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`line-level-${level}`}>
                            <Checkbox checked={lc.enabled} onCheckedChange={(v) => updateLevel('enabled', !!v)} />
                            Enable Lines for Level {level}
                          </label>
                          {lc.enabled && (
                            <div className="flex gap-2 pl-6">
                              {['solid', 'dashed'].map(style => (
                                <button key={style} type="button" onClick={() => updateLevel('style', style)}
                                  className={cn("flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all",
                                    lc.style === style ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : cn(colors.borderPrimary, colors.bgSecondary)
                                  )} data-testid={`line-style-${level}-${style}`}>
                                  <div className={cn("w-6 border-t-2", style === 'dashed' ? 'border-dashed' : 'border-solid')} style={{ borderColor: formData.branding_overrides.secondary_color || '#c4b5fd' }} />
                                  {style.charAt(0).toUpperCase() + style.slice(1)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="colors" className="mt-3 space-y-3">
                  <ColorInput label="Background" value={formData.branding_overrides.background_color} onChange={(v) => updateBranding('background_color', v)} placeholder="SR default" srDefault={SR_DEFAULTS.background_color} colors={colors} />
                  <div className="grid grid-cols-2 gap-3">
                    <ColorInput label="Icon / Bullet" value={formData.branding_overrides.primary_color} onChange={(v) => updateBranding('primary_color', v)} placeholder="#8b5cf6" colors={colors} />
                    <ColorInput label="Lines" value={formData.branding_overrides.secondary_color} onChange={(v) => updateBranding('secondary_color', v)} placeholder="#c4b5fd" colors={colors} />
                    <ColorInput label="Text" value={formData.branding_overrides.text_color} onChange={(v) => updateBranding('text_color', v)} placeholder="SR default" srDefault={SR_DEFAULTS.text_color} colors={colors} />
                    <ColorInput label="Date" value={formData.branding_overrides.date_color} onChange={(v) => updateBranding('date_color', v)} placeholder="SR default" srDefault={SR_DEFAULTS.date_color} colors={colors} />
                  </div>
                </TabsContent>

                <TabsContent value="logo" className="mt-3">
                  {organization?.logo_url ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <img src={organization.logo_url} alt="Org logo" className="h-8 w-auto rounded" />
                        <span className={cn("text-xs", colors.textMuted)}>From organization settings</span>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Position</Label>
                        <div className="flex gap-2">
                          {[{ value: 'left', icon: AlignLeft }, { value: 'center', icon: AlignCenter }, { value: 'right', icon: AlignRight }].map(pos => {
                            const PosIcon = pos.icon;
                            return (
                              <button key={pos.value} type="button" onClick={() => updateBranding('logo_position', pos.value)}
                                className={cn("flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm transition-all",
                                  formData.branding_overrides.logo_position === pos.value ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : cn(colors.borderPrimary, colors.bgSecondary)
                                )} data-testid={`logo-pos-${pos.value}`}><PosIcon className="w-4 h-4" /> {pos.value.charAt(0).toUpperCase() + pos.value.slice(1)}</button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={cn("p-4 rounded-lg text-center", colors.bgSecondary)}>
                      <p className={cn("text-sm", colors.textMuted)}>Upload a logo in Organization Settings to display it on your visualization.</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Save as Template */}
              <div className="pt-3 mt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                {!showSaveTemplate ? (
                  <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate(true)} className="gap-1.5 w-full" data-testid="save-as-template-btn">
                    <Save className="w-3.5 h-3.5" /> Save as Template
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Input value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} placeholder="Template name..." className={cn("flex-1 h-8 text-sm", colors.bgSecondary)} autoFocus data-testid="template-name-input" />
                    <Button size="sm" onClick={handleSaveAsTemplate} disabled={savingTemplate} data-testid="confirm-save-template-btn">
                      {savingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setShowSaveTemplate(false); setNewTemplateName(''); }}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Access Control Tab */}
            <TabsContent value="access" className="mt-0 space-y-6">
              <div><Label>Who can view this visualization?</Label><p className={cn("text-xs mt-1 mb-4", colors.textMuted)}>Control access to the published visualization.</p></div>
              <div className="space-y-3">
                {ACCESS_OPTIONS.map(option => {
                  const Icon = option.icon;
                  const isSelected = formData.access_control === option.value;
                  return (
                    <button key={option.value} type="button" onClick={() => setFormData(prev => ({ ...prev, access_control: option.value }))}
                      className={cn("w-full p-4 rounded-lg border text-left transition-all",
                        isSelected ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : cn(colors.borderPrimary, colors.bgSecondary, "hover:border-purple-500/50")
                      )}>
                      <div className="flex items-center gap-3">
                        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", isSelected ? "bg-purple-500 text-white" : "bg-gray-100 dark:bg-gray-800")}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className={cn("font-medium", colors.textPrimary)}>{option.label}</p>
                          <p className={cn("text-sm", colors.textMuted)}>{option.description}</p>
                        </div>
                        <div className={cn("w-5 h-5 rounded-full border flex items-center justify-center", isSelected ? "bg-purple-500 border-purple-500" : colors.borderPrimary)}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {formData.access_control === 'selected_users' && (
                <div className="space-y-3 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <Label>Select Users</Label>
                  {loadingData ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading users...</div>
                  ) : orgUsers.length === 0 ? (
                    <p className={cn("text-sm", colors.textMuted)}>No other users in your organization.</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {orgUsers.map(user => {
                        const isSelected = formData.allowed_user_ids.includes(user.user_id);
                        return (
                          <button key={user.user_id} type="button" onClick={() => toggleUser(user.user_id)}
                            className={cn("w-full p-3 rounded-lg border text-left transition-all flex items-center gap-3",
                              isSelected ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : cn(colors.borderPrimary, colors.bgSecondary)
                            )}>
                            <div className={cn("w-5 h-5 rounded border flex items-center justify-center", isSelected ? "bg-purple-500 border-purple-500" : colors.borderPrimary)}>
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={cn("font-medium text-sm truncate", colors.textPrimary)}>{user.name || user.email}</p>
                              {user.name && <p className={cn("text-xs truncate", colors.textMuted)}>{user.email}</p>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className={cn("text-sm", colors.textSecondary)}>{formData.allowed_user_ids.length} user{formData.allowed_user_ids.length !== 1 ? 's' : ''} selected</p>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VisualizationSettingsModal;
